class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.cartErrors = document.getElementById('cart-errors');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    if (event.target === null) return;
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  getSectionsToRender() {
    let sections = [
      {
        id: 'mini-cart',
        section: document.getElementById('mini-cart')?.id,
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      }
    ];
    if (document.querySelector('#main-cart-footer .free-shipping')) {
      sections.push({
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.free-shipping',
      });
    }
    return sections;
  }

  /*
    CRT-02 - what the visitor is told, and where focus goes.

    The line's own name has to be read before the request, because the response
    replaces the markup wholesale and by then the row may not exist at all.
  */
  describeLine(line) {
    const row =
      this.querySelector(`#CartItem-${line}`) || this.querySelectorAll('[data-variant-id]')[line - 1];
    if (!row) return '';
    // The drawer and the cart page name the product differently, and the drawer's title
    // element also carries the quantity and an upper title. Read the innermost title
    // span first, then the anchor, and never fall back to the whole row - that produced
    // "Nourishing Eye Cream x2 $73.00 Quantity Decrease quantity for..." as the name.
    const named =
      row.querySelector('.mini-cart-product-title__title span:not([class])') ||
      row.querySelector('.cart-item__name') ||
      row.querySelector('.mini-cart-product-title') ||
      row.querySelector('a[href*="/products/"]');
    if (!named) return '';
    const text = (named.cloneNode(true) || named);
    text.querySelectorAll?.('.mini-cart-product-title__title-quantity').forEach((n) => n.remove());
    return (text.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 80);
  }

  updateQuantity(line, quantity, name) {
    const itemName = this.describeLine(line);
    const removing = parseInt(quantity, 10) === 0;
    this.enableLoading(line);
    const sections = this.getSectionsToRender().map((section) => section.section);

    const body = JSON.stringify({
      line,
      quantity,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(`${window.routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        /*
          CRT-03 - a rejected or capped change comes back 422 with no `sections`, and the
          old code went straight on to read `parsedState.sections[...]`. That threw a
          TypeError and landed in .catch(), so Shopify's own explanation ("Only 50 items
          were added to your cart due to availability") was discarded.

          This guard is deliberately narrow - `status` only, not `!response.ok` and not
          the absence of `sections`. Both of those looked like safer tests and both broke
          the success path when tried: a successful change on /cart can legitimately come
          back without `sections`, because getSectionsToRender() puts undefined entries in
          the request when the drawer is not on the page. Measured, not assumed. FND-67
          records the remaining gap: a 422 whose body carries no `status` still falls
          through to the generic .catch() message.
        */
        if (parsedState.status) {
          this.handleUpdateError(line, parsedState.description || window.cartStrings.error);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const element = document.getElementById(section.id);
          if (element) {
            const elementToReplace = element.querySelector(section.selector) || element;

            if (elementToReplace && parsedState.sections[section.section]) {
              elementToReplace.innerHTML =
                this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }
        }));

        const acceptedQty = Array.isArray(parsedState.items) && parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        this.updateLiveRegions(line, parsedState.item_count, parseInt(quantity, 10), acceptedQty);
        this.announceChange(parsedState, itemName, removing, quantity, line);

        const lineItem = document.getElementById(`CartItem-${line}`);
        // `name` is read from document.activeElement 300ms earlier, behind the debounce,
        // so it can be gone by now. The row's own quantity field is the right fallback -
        // and the original `lineItem.querySelector(...).focus()` threw outright when the
        // selector matched nothing.
        const control = lineItem
          ? (name && lineItem.querySelector(`[name="${name}"]`)) ||
            lineItem.querySelector('[name="updates[]"]')
          : null;
        if (control) {
          control.focus();
        } else if (removing || !lineItem) {
          // The row this control belonged to is gone. Without this the caret is left
          // on <body> and the visitor has to tab the whole page again (SC 2.4.3).
          this.restoreFocusAfterRemoval(line);
        }
        this.disableLoading();

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: state
          }
        }));
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        this.disableLoading();
        /*
          CRT-03 - a failed update wrote into #cart-errors and stopped there. That
          container now carries role="alert", and the message also goes through the shared
          status region, because #cart-errors lives in main-cart-footer and the drawer
          does not render it at all (SC 4.1.3).
        */
        if (this.cartErrors) {
          this.cartErrors.textContent = window.cartStrings.error;
        }
        this.announce(window.cartStrings.error);
        this.resyncQuantityField(line);
      });
  }

  /*
    CRT-03 - a rejected or capped change. The message goes into the line's own error
    container, which carries role="alert" and is referenced by the quantity field's
    aria-describedby, and through the shared status region so the drawer is covered too.
  */
  handleUpdateError(line, description) {
    this.disableLoading();
    this.querySelectorAll('.loading-overlay').forEach((o) => o.classList.add('hidden'));

    const container = document.getElementById(`Line-item-error-${line}`);
    const text = container && container.querySelector('.cart-item__error-text');
    if (text) text.textContent = description;

    // The container carries role="alert" now that FND-66 is fixed, so it speaks on its
    // own. The shared region is announced too, because #a11y-cart-status is the only
    // status surface the drawer has - it renders no per-line error container at all.
    this.announce(description);
    this.resyncQuantityField(line);
  }

  /*
    The field keeps whatever was typed, because nothing re-renders on an error path. It
    then disagrees with the cart, and a screen reader reads the number that was refused.
    The cart itself is the only trustworthy source - Shopify may have capped rather than
    rejected, so neither the typed value nor the previously rendered one is correct.
  */
  resyncQuantityField(line) {
    const field =
      document.querySelector(`#CartItem-${line} [name="updates[]"]`) ||
      this.querySelectorAll('[name="updates[]"]')[line - 1];
    if (!field) return;

    fetch(`${window.routes.cart_url}.js`)
      .then((r) => r.json())
      .then((cart) => {
        const item = cart.items && cart.items[line - 1];
        if (item && typeof item.quantity === 'number') field.value = item.quantity;
      })
      .catch(() => {
        field.value = field.defaultValue;
      });
  }

  /*
    CRT-03 / FND-66 - this used to infer "the server refused the change" from
    `this.currentItemCount === itemCount`. That comparison stopped meaning what it was
    written to mean: the section re-render replaces this element's contents, a fresh
    instance reads `currentItemCount` back from the new DOM, and the two counts then
    match on perfectly successful updates. The result was "You can only add 3 of this
    item" written into the line's error container after a successful 1 to 3 change -
    measured. It was invisible only because nothing announced that container.

    The response already carries the answer, so the refusal test is now direct: what was
    asked for against what the cart actually holds.
  */
  updateLiveRegions(line, itemCount, requested, accepted) {
    const container = document.getElementById(`Line-item-error-${line}`);
    const text = container && container.querySelector('.cart-item__error-text');

    if (text) {
      const refused =
        typeof accepted === 'number' && typeof requested === 'number' && accepted !== requested;
      text.textContent = refused
        ? window.cartStrings.quantityError.replace('[quantity]', accepted)
        : '';
    }

    this.currentItemCount = itemCount;
    
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1e3);
    }
  }

  /*
    CRT-02: quantity changes and removals announced nothing in the drawer, because
    #cart-live-region-text and #shopping-cart-line-item-status only ever existed in
    sections/main-cart-items.liquid. #a11y-cart-status lives in the layout, outside
    every element the cart replaces, so it survives the re-render and announces on
    both surfaces.
  */
  announce(message) {
    const region = document.getElementById('a11y-cart-status');
    if (!region || !message) return;
    // Clear first so an identical repeat message still registers as a change.
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  announceChange(parsedState, itemName, removing, quantity, line) {
    const strings = window.cartStrings || {};
    if (removing) {
      if (parsedState.item_count === 0 && strings.cartEmpty) return this.announce(strings.cartEmpty);
      if (strings.itemRemoved) this.announce(strings.itemRemoved.replace('[product]', itemName));
      return;
    }
    if (!strings.quantityUpdated) return;

    // The subtotal is read from the page rather than formatted here, because the shop's
    // money format is Liquid's to decide. When it cannot be read, the sentence drops it
    // instead of announcing a bare number in no currency.
    /*
      CRT-03 - Shopify caps a quantity at available inventory, so the number the visitor
      typed and the number the cart now holds are not always the same. Announcing the
      requested one told them the opposite of what happened; the accepted value is read
      back from the response.
    */
    const acceptedLine = Array.isArray(parsedState.items)
      ? parsedState.items[parseInt(line, 10) - 1]
      : null;
    const accepted =
      acceptedLine && typeof acceptedLine.quantity === 'number' ? acceptedLine.quantity : quantity;

    const total = this.readSubtotal();
    let message = strings.quantityUpdated
      .replace('[product]', itemName)
      .replace('[quantity]', accepted);
    message = total
      ? message.replace('[total]', total)
      : message.replace(/\s*[^.]*\[total\][^.]*\./, '');
    this.announce(message.trim());
  }

  readSubtotal() {
    const candidates = document.querySelectorAll(
      '.totals__subtotal-value, .totals__total-value, #mini-cart-subtotal, .subtotal .value'
    );
    for (const node of candidates) {
      const text = (node.textContent || '').trim();
      // Must hold digits and must not end on a separator: the drawer splits its subtotal
      // across elements, and the first of them reads "$73." - announcing that would be
      // worse than announcing no total at all.
      if (/\d/.test(text) && !/[.,]$/.test(text)) return text;
    }
    return '';
  }

  /*
    CRT-02: after a removal the control that had focus no longer exists. Focus goes to
    the row that took its place, then to the row before it, then to the cart's own
    heading - never to <body>.
  */
  restoreFocusAfterRemoval(line) {
    // Queried from the document, not from `this`: the drawer replaces the whole
    // #mini-cart section, which detaches this very element. Focusing a detached node
    // silently does nothing and the caret ends up on <body>.
    const scope = document.getElementById('mini-cart') || document;
    const rows = scope.querySelectorAll('[id^="CartItem-"], .mini-cart__navigation > li');
    const next = rows[line - 1] || rows[rows.length - 1];
    // A selector list matches in document order, not in order of preference, so the
    // image link won over the quantity field. These are tried one at a time.
    const preferred = ['[name="updates[]"]', 'cart-remove-button a, cart-remove-button button', 'a[href*="/products/"]', 'a[href], button'];
    let inRow = null;
    if (next) {
      for (const sel of preferred) {
        inRow = next.querySelector(sel);
        if (inRow) break;
      }
    }
    const target =
      inRow ||
      // Emptying the cart replaces the whole region, so the last resort is the region
      // itself. It always exists, which <body> is not an acceptable substitute for.
      document.querySelector('.cart__empty-text, .mini-cart__empty-text') ||
      document.querySelector('#main-cart-items, #mini-cart');
    if (!target) return;
    if (!target.matches('a[href], button, input, select, textarea') && !target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus();
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML;
  }

  enableLoading(line) {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.add('cart__items--disabled');

    const loadingOverlay = this.querySelectorAll('.loading-overlay')[line - 1];
    if (loadingOverlay) loadingOverlay.classList.remove('hidden');
    
    // Blurring unconditionally threw focus to <body> on every cart change, including
    // changes made from somewhere else on the page entirely.
    if (document.activeElement && this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.remove('cart__items--disabled');
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section => {
      const element = document.getElementById(section.id);

      if (element) {
        element.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));
  }
}
customElements.define('cart-items', CartItems);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', debounce((event) => {
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${window.routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
    }, 300));
  }
}
customElements.define('cart-note', CartNote);

class DiscountCode extends HTMLElement {
  constructor() {
    super();

    if (isStorageSupported('session')) {
      this.setupDiscount();

      this.addEventListener('change', (event) => {
        window.sessionStorage.setItem('discount', event.target.value);
      });
    }
  }

  setupDiscount() {
    const discount = window.sessionStorage.getItem('discount');
    if (discount !== null) {
      this.querySelector('input[name="discount"]').value = discount;
    }
  }
}

customElements.define('discount-code', DiscountCode);

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    this.setupCountries();
    
    this.errors = this.querySelector('#ShippingCalculatorErrors');
    this.success = this.querySelector('#ShippingCalculatorSuccess');
    this.zip = this.querySelector('#ShippingCalculatorZip');
    this.country = this.querySelector('#ShippingCalculatorCountry');
    this.province = this.querySelector('#ShippingCalculatorProvince');
    this.button = this.querySelector('button');
    this.button.addEventListener('click', this.onSubmitHandler.bind(this));
  }

  setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      // eslint-disable-next-line no-new
      new Shopify.CountryProvinceSelector('ShippingCalculatorCountry', 'ShippingCalculatorProvince', {
        hideElement: 'ShippingCalculatorProvinceContainer'
      });
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.button.classList.contains('loading')) return;
    
    this.errors.classList.add('hidden');
    this.success.classList.add('hidden');
    this.errors.querySelector('.errors').textContent = '';
    this.success.textContent = '';
    this.zip.classList.remove('invalid');
    this.country.classList.remove('invalid');
    this.province.classList.remove('invalid');
    this.button.classList.add('loading');
    /*
      CRT-03 - `disabled` removes the button from the tab order, so the browser dropped
      focus to <body> the moment the request started and the visitor lost their place
      (SC 2.4.3). `aria-busy` reports the same thing without taking the control away;
      the `loading` class below guards against a second submission.
    */
    this.button.setAttribute('aria-busy', 'true');

    const body = JSON.stringify({
      shipping_address: {
        zip: this.zip.value,
        country: this.country.value,
        province: this.province.value
      }
    });
    let sectionUrl = `${window.routes.cart_url}/shipping_rates.json`;

    // remove double `/` in case shop might have /en or language in URL
    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          /*
            CRT-03 - the region is revealed first and filled on the next frame. A live
            region that is `display: none` when its content changes announces nothing,
            so doing both in one go would have left the roles decorative.
          */
          this.success.classList.remove('hidden');
          this.success.innerHTML = '';

          requestAnimationFrame(() => {
            parsedState.shipping_rates.forEach((rate) => {
              const child = document.createElement('p');
              child.textContent = `${rate.name}: ${rate.price} ${Shopify.currency.active}`;
              this.success.appendChild(child);
            });
          });
        }
        else {
          let errors = [];
          Object.entries(parsedState).forEach(([attribute, messages]) => {
            errors.push(`${attribute.charAt(0).toUpperCase() + attribute.slice(1)} ${messages[0]}`);
          });

          this.errors.classList.remove('hidden');
          const target = this.errors.querySelector('.errors');
          requestAnimationFrame(() => {
            target.textContent = errors.join('; ');
          });
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('aria-busy');
      });
  }
}

customElements.define('shipping-calculator', ShippingCalculator);
